import { Calendar, Gift, Newspaper, Pencil, RefreshCw } from 'lucide-react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { useState } from 'react';
import type { GbpPostType } from '@pageboss/shared';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { api, type ApiError } from '@/lib/api';
import type { GbpPostRowWire } from '@/lib/types';
import { cn } from '@/lib/utils';
import type { GbpPostEditTarget } from './post-compose';

// Real gbp_posts rows: what the owner sent through pageboss, with status.
// No views/clicks — the post-metrics API is not wired (S4+); this list shows
// only what we actually know. lastError is an error CLASS; map it, never
// render it raw.
//
// 'draft' rows with a lastError are a TRANSIENT failure (network/quota/
// upstream, or a stale Google connection) — the saved text was fine, so a
// blind "Try again" resend is enough (routes/api/gbp.ts sendPostRow
// comment). 'failed' rows are a PERMANENT content rejection — Google
// declined this exact text, so retrying it unchanged cannot succeed
// (screen-design addendum §B); those get "Edit & resend" instead, which
// hands the compose panel above this list the row's type + text to fix.
// 'sending' is neither — a send is in flight for it right now, so no
// button renders at all, just the "Not on Google yet" badge.

interface GbpPostsListProps {
  posts: GbpPostRowWire[];
  siteId: string;
  canWrite: boolean;
  onChanged: (announcement?: string) => void;
  onEditRequest: (target: GbpPostEditTarget) => void;
}

const TYPE_META: Record<GbpPostType, { label: string; tone: string; Icon: typeof Newspaper }> = {
  update: { label: 'Update', tone: 'bg-surface-sunken text-text-secondary', Icon: Newspaper },
  offer: { label: 'Offer', tone: 'bg-success-bg text-success-fg', Icon: Gift },
  event: { label: 'Event', tone: 'bg-brand-50 text-brand-700', Icon: Calendar },
};

// Never render lastError raw — map to a plain sentence. Covers every class
// sendPostRow can write (rejected/network/upstream/unauthorized/quota/
// forbidden all collapse to the same honest "couldn't reach Google" for a
// draft row; 'rejected' only ever appears on a 'failed' row).
function failureNote(status: GbpPostRowWire['status'], lastError: string | null): string | null {
  if (status === 'failed') return "Google didn't accept this post.";
  if (status !== 'draft' || lastError === null) return null;
  if (lastError === 'unauthorized') return 'Waiting for your Google connection to be renewed.';
  return "Couldn't reach Google — try sending again.";
}

// Retry-button error map — send_in_progress/already_posted are only
// reachable here (a race: another tab already claimed or finished the
// send); post_rejected/gbp_unreachable/reauth_needed can also come back
// from this same /send endpoint on a fresh attempt.
const RETRY_ERRORS: Record<string, string> = {
  post_rejected: "Google still didn't accept this post. Use Edit & resend to change the text.",
  gbp_unreachable: "Still couldn't reach Google. Try again in a moment.",
  reauth_needed: 'Your Google connection needs to be renewed. Reconnect from the top of this page.',
  send_in_progress: 'That post is already being sent — give it a second and refresh.',
  already_posted: 'That post already went out — refresh to see it.',
  locked: "Your current plan doesn't include this anymore. Check your plan in Billing.",
  not_verified: "Google hasn't finished verifying this listing yet, so changes can't be published.",
  not_connected: "This site's Google connection was removed. Reconnect from the top of this page.",
  gbp_unavailable: "Google features aren't available right now. Try again later.",
  not_scheduled: 'That post is no longer scheduled — refresh to see its current state.',
};
const RETRY_FALLBACK = "Still couldn't send it. Try again in a moment.";
function retryErrorMessage(code: string | undefined): string {
  return (code && RETRY_ERRORS[code]) ?? RETRY_FALLBACK;
}
function apiErrorCode(err: unknown): string | undefined {
  const body = (err as ApiError | undefined)?.body;
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : undefined;
}

export function GbpPostsList({ posts, siteId, canWrite, onChanged, onEditRequest }: GbpPostsListProps) {
  if (posts.length === 0) {
    return <p className="text-sm text-text-tertiary">Nothing posted from pageboss yet.</p>;
  }
  return (
    <ul className="flex flex-col gap-3">
      {posts.map((p) => (
        <li key={p.id}>
          <PostCard post={p} siteId={siteId} canWrite={canWrite} onChanged={onChanged} onEditRequest={onEditRequest} />
        </li>
      ))}
    </ul>
  );
}

function PostCard({
  post,
  siteId,
  canWrite,
  onChanged,
  onEditRequest,
}: {
  post: GbpPostRowWire;
  siteId: string;
  canWrite: boolean;
  onChanged: (announcement?: string) => void;
  onEditRequest: (target: GbpPostEditTarget) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const meta = TYPE_META[post.type];
  const Icon = meta.Icon;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const note = failureNote(post.status, post.lastError);

  async function tryAgain() {
    setBusy(true);
    setError(null);
    try {
      await api.post(locationApi(businessId, siteId, `gbp/posts/${post.id}/send`), {});
      onChanged('Posted to Google.');
    } catch (err) {
      setError(retryErrorMessage(apiErrorCode(err)));
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  // Reversible — no confirm modal (addendum §B): the row falls back to a
  // clean 'draft', the text is untouched, and the success announcement says
  // so plainly. "Try again" on that draft row is the send-it-after-all path.
  // FIX 5 (S4 review, Important): a recurring row's success message says so
  // explicitly — canceling a repeating post silently reading the same
  // one-time "saved as a draft" copy understated what just happened (the
  // WHOLE future series stops, not just this one occurrence).
  async function cancelSchedule() {
    setBusy(true);
    setError(null);
    try {
      await api.post(locationApi(businessId, siteId, `gbp/posts/${post.id}/cancel`), {});
      onChanged(
        post.recurrence
          ? 'Recurring series canceled — the post is saved as a single draft.'
          : 'Scheduled post canceled — saved as a draft.',
      );
    } catch (err) {
      setError(retryErrorMessage(apiErrorCode(err)));
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="rounded-md border border-border-subtle bg-surface-page p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
            meta.tone,
          )}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {meta.label}
        </span>
        {post.status === 'posted' ? (
          <Badge variant="success">Posted</Badge>
        ) : post.status === 'scheduled' ? (
          // Neutral, not warning (addendum §B): a scheduled post is the
          // owner's own deliberate choice, sitting fine — the amber tone is
          // reserved for rows that need the owner's attention.
          <Badge variant="muted">Scheduled</Badge>
        ) : (
          <Badge variant="warning">Not on Google yet</Badge>
        )}
        <span className="text-xs text-text-tertiary">
          {post.status === 'posted' && post.postedAt
            ? `Posted ${formatDate(post.postedAt)}`
            : `Saved ${formatDate(post.createdAt)}`}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-text-secondary">{post.summary}</p>
      {note ? <p className="mt-2 text-sm text-danger-fg">{note}</p> : null}
      {error ? (
        <p className="mt-2 text-sm text-danger-fg" role="alert">
          {error}
        </p>
      ) : null}
      {canWrite && post.status === 'draft' ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void tryAgain()}
          className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'mt-3')}
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          {busy ? 'Sending…' : 'Try again'}
        </button>
      ) : null}
      {canWrite && post.status === 'failed' ? (
        <button
          type="button"
          onClick={() =>
            onEditRequest({
              type: post.type,
              summary: post.summary,
              ...(post.payload ? { payload: post.payload } : {}),
              // FIX 5: a rejected recurring row keeps its recurrence rule —
              // hand it to the compose panel so "Edit & resend" restores the
              // owner's cadence prefs, not just the text.
              ...(post.recurrence ? { recurrence: post.recurrence } : {}),
            })
          }
          className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'mt-3')}
        >
          <Pencil className="h-3 w-3" aria-hidden />
          Edit & resend
        </button>
      ) : null}
      {post.status === 'sending' ? (
        // A send is in flight right now — nothing for the owner to do this
        // second, so no button renders at all.
        <p className="mt-3 text-xs text-text-tertiary">Sending to Google…</p>
      ) : null}
      {post.status === 'scheduled' ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-xs text-text-tertiary">
            {post.scheduledAt
              ? `Goes out ${formatDateTime(post.scheduledAt)}.`
              : 'Waiting to be sent.'}
            {/* FIX 5 (S4 review, Important): a recurring row's cadence was
                previously invisible on this card — an owner had no way to
                tell "one-time" and "repeats forever" apart, and "Cancel
                schedule" read as canceling just this occurrence. */}
            {post.recurrence
              ? ` Repeats ${post.recurrence.cadence}${
                  post.recurrence.until ? ` until ${formatIsoDate(post.recurrence.until)}` : ''
                }. Canceling stops all future posts.`
              : ''}
          </p>
          {canWrite ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void cancelSchedule()}
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              {busy ? 'Canceling…' : post.recurrence ? 'Cancel series' : 'Cancel schedule'}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// A recurrence rule's `until` is a plain YYYY-MM-DD calendar date (the
// owner's, per FIX 4), not a UTC instant — format it with an explicit UTC
// timeZone so a viewer west of UTC doesn't see it silently roll back a day.
function formatIsoDate(iso: string): string {
  const parts = iso.split('-').map(Number) as [number, number, number];
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
